import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Empty, notification, Select, Space, Spin, Tooltip } from 'antd'

import i18n from 'i18next'

import { refreshSlide } from '@/store/slices/slides/actions'
import { AppDispatch } from '@/store/store'
import {
  selectCanvasSelectedObjectDataDefinition
} from '@store/slices/shapes/selectors'
import { DefaultDataDefinition } from '@store/slices/shapes/types'

import {
  createDataAction,
  deleteDataAction,
  getDataActions,
  updateDataAction
} from '@services/data-actions-service'

import { isInSequenceNetsEnabled } from '@/utils'

import { useDataTransformation, useRowsNColumns } from '../hooks'
import { getResultingRowsnColumns } from '../utils'
import ActionList from './ActionList'
import CopyOrder from './CopyOrder'
import DeleteByLabel from './DeleteByLabel'
import DeletePosition from './DeletePosition'
import Filter from './Filter'
import Reorder from './Reorder'
import Reverse from './Reverse'
import Round from './Round'
import ShiftLabels from './ShiftLabels'
import SortBy from './SortBy'
import StringFilter from './StringFilter'
import SumValues from './SumValues'
import PctUplift from './PctUplift'
import PointChange from './PointChange'
import Transpose from './Transpose'
import Trim from './Trim'
import { Action, ActionTypes } from './types'

const DataTransformationsSelector = () => {
  const dispatch: AppDispatch = useDispatch()
  // A shape with no data mapped into its Output view makes this selector return null. Destructuring
  // that null threw during render and took down the whole editor (issue #735), so read the definition
  // first and fall back to an empty object; the guarded early-return below shows a friendly message
  // instead of the transformation form when there is nothing to transform.
  const dataDefinition = useSelector(
    selectCanvasSelectedObjectDataDefinition
  ) as DefaultDataDefinition | null
  const { transformationId, tableId } = dataDefinition ?? ({} as DefaultDataDefinition)
  const { rows, columns, mutate: mutateLabels } = useRowsNColumns(tableId)

  const [selectedAction, setSelectedAction] = useState<string>(null)
  const [actionQueue, setActionQueue] = useState([])
  const { dataTransformation, mutate } = useDataTransformation(transformationId)
  const [loading, setLoading] = useState(false)
  const [initialValues, setInitialValues] = useState(null)
  const [mode, setMode] = useState(null)
  const [clearOptions, setClearOptions] = useState({
    filter: false,
    reorder: false,
    reverse: false,
    sort_by: false,
    transpose: false,
    top_and_bottom: false,
    copy_order: false,
    string_filter: false,
    shift_labels: false,
    sum_values: false,
    pct_uplift: false,
    delete_position: false,
    delete_label: false,
    point_change: false,
    round: false
  })

  const [resultingRows, resultingColumns] = getResultingRowsnColumns(
    rows,
    columns,
    dataTransformation
  )

  // In-sequence nets (issue #1130) are picked from the output frame, like Sort by; materialized
  // nets keep picking from the source-table label list. Must match the backend flag.
  const inSequenceNets = isInSequenceNetsEnabled()
  const netColumns = inSequenceNets ? resultingColumns : columns
  const netRows = inSequenceNets ? resultingRows : rows

  const [transformVisible, setTransformVisible] = useState(false)

  const tooltips = {
    [ActionTypes.FILTER]:
      'Remove columns or rows which contain values above, below or between the level set in the rule.',
    [ActionTypes.REORDER]: 'Set the order of rows and columns manually.',
    [ActionTypes.REVERSE]: 'Reverse the order of rows or columns.',
    [ActionTypes.SORTBY]: 'Set the order of rows or columns by ascending or descending order.',
    [ActionTypes.TRANSPOSE]: 'Switch the rows or columns in the mapped data object.',
    [ActionTypes.TOP_AND_BOTTOM]:
      'Limit the number of rows or columns that are mapped into the object by setting a numerical figure.',
    [ActionTypes.COPY_ORDER]:
      'Take the order of rows or columns from any mapped data object and apply to another mapped data object.',
    [ActionTypes.STRING_FILTER]:
      'Remove columns or rows which contain a set of characters in their names.',
    [ActionTypes.SHIFT_LABELS]: 'Shift rows/columns to start/end of table.',
    [ActionTypes.SUM_VALUES]: 'Sum values along rows or columns.',
    [ActionTypes.PCT_UPLIFT]: 'Output the percentage uplift of a comparator over a control.',
    [ActionTypes.DELETE_POSITION]: 'Delete selected rows or columns by their position in the output.',
    [ActionTypes.DELETE_LABEL]: 'Delete rows or columns by their label from the output.',
    [ActionTypes.POINT_CHANGE]:
      'Output the percentage-point change (comparator minus control) as a new column or row.',
    [ActionTypes.ROUND]: 'Round values to a chosen number of decimal places.'
  }
  const actionMapping = {
    [ActionTypes.FILTER]: i18n.t(
      'editor.right-panel.data-source.form.data-transformation.actions.filter'
    ),
    [ActionTypes.REORDER]: i18n.t(
      'editor.right-panel.data-source.form.data-transformation.actions.reorder'
    ),
    [ActionTypes.REVERSE]: i18n.t(
      'editor.right-panel.data-source.form.data-transformation.actions.reverse'
    ),
    [ActionTypes.SORTBY]: i18n.t(
      'editor.right-panel.data-source.form.data-transformation.actions.sort-by'
    ),
    [ActionTypes.TRANSPOSE]: i18n.t(
      'editor.right-panel.data-source.form.data-transformation.actions.transpose'
    ),
    [ActionTypes.TOP_AND_BOTTOM]: i18n.t(
      'editor.right-panel.data-source.form.data-transformation.actions.trim'
    ),
    [ActionTypes.COPY_ORDER]: i18n.t(
      'editor.right-panel.data-source.form.data-transformation.actions.rearrange'
    ),
    [ActionTypes.STRING_FILTER]: i18n.t(
      'editor.right-panel.data-source.form.data-transformation.actions.string_filter'
    ),
    [ActionTypes.SHIFT_LABELS]: i18n.t(
      'editor.right-panel.data-source.form.data-transformation.actions.shift_labels'
    ),
    [ActionTypes.SUM_VALUES]: i18n.t(
      'editor.right-panel.data-source.form.data-transformation.actions.sum_values'
    ),
    [ActionTypes.PCT_UPLIFT]: '% Uplift',
    [ActionTypes.DELETE_POSITION]: i18n.t(
      'editor.right-panel.data-source.form.data-transformation.actions.delete_position'
    ),
    [ActionTypes.DELETE_LABEL]: i18n.t(
      'editor.right-panel.data-source.form.data-transformation.actions.delete_label'
    ),
    [ActionTypes.POINT_CHANGE]: '% Point Change',
    [ActionTypes.ROUND]: i18n.t(
      'editor.right-panel.data-source.form.data-transformation.actions.round'
    )
  }

  const openNotification = error => {
    notification.warning({
      key: error,
      duration: 5,
      message: 'Sorry, an error occurred',
      description:
        'We encountered the following error when trying to apply your data transformation: ' + error
    })
  }

  const getErrorMessage = error => {
    if (error?.response?.data?.nonFieldErrors?.[0]) {
      return error.response.data.nonFieldErrors[0]
    }
    if (Array.isArray(error?.response?.data) && error.response.data[0]) {
      return error.response.data[0]
    }
    if (error?.response?.request?.responseText) {
      return error.response.request.responseText
    }
    return error?.message
  }

  const onSelect = (value: ActionTypes) => {
    const opts = clearOptions
    opts[value] = true
    setClearOptions({ ...opts })
    setInitialValues(null)
    setSelectedAction(value)
    setMode('create')
  }

  const onCancel = () => {
    setSelectedAction(null)
  }

  const onSubmit = async ({
    actionType,
    ...values
  }: Omit<Action<typeof actionType>, 'shapeDataSelection'>) => {
    try {
      setLoading(true)
      if (mode === 'create') {
        const response = await createDataAction({
          shapeDataSelection: transformationId,
          specs: {},
          by: [],
          actionType,
          ...values
        })
        dispatch(refreshSlide())

        setActionQueue(prev => [...prev, response])
      }
      if (mode === 'update') {
        const response = await updateDataAction(initialValues.id, {
          shapeDataSelection: transformationId,
          specs: {},
          by: [],
          actionType,
          ...values
        })
        dispatch(refreshSlide())
        setActionQueue(prev => [
          ...prev.map(action => (action.id !== response.id ? action : response))
        ])
      }
      mutate()
      // A materialized net is a real column/row now (issue #663); refresh the cached label list
      // so Sort by / Reorder / Filter / etc. show the new (or renamed) net without a page reload.
      if (
        actionType === ActionTypes.SUM_VALUES ||
        actionType === ActionTypes.PCT_UPLIFT ||
        actionType === ActionTypes.POINT_CHANGE
      ) {
        mutateLabels()
      }
      const opts = clearOptions
      opts[actionType] = false
      setClearOptions({ ...opts })
      setLoading(false)
    } catch (error) {
      openNotification(getErrorMessage(error))
      console.error(error.response)
      setLoading(false)
    }
    setSelectedAction(null)
    setInitialValues(null)
  }

  const onCopyOrderSubmit = () => {
    const opts = clearOptions
    opts['copy_order'] = false
    setClearOptions({ ...opts })
    dispatch(refreshSlide())
  }

  const onEditAction = (action: Action) => {
    const opts = clearOptions
    opts[action.actionType] = true
    setClearOptions({ ...opts })
    setMode('update')
    // Always set output_name for sum_values
    let outputName =
      (action as any).output_name ||
      (action as any).outputName ||
      (action as any).specs?.name ||
      (action as any).name ||
      ''
    console.log('Setting output_name for edit:', outputName)
    setInitialValues({ ...action, ...action.specs, output_name: outputName })
    setSelectedAction(action.actionType)
    fetchActions()
  }

  const onDeleteAction = async (id: number, actionType: string) => {
    try {
      const response = await deleteDataAction(id)
      const opts = clearOptions
      opts[actionType] = true
      setClearOptions({ ...opts })
      setInitialValues(null)
      //@ts-ignore
      dispatch(refreshSlide())
      mutate()
      // Deleting a materialized net removes its real column/row (issue #663), so refetch the
      // cached label list too, otherwise the removed net lingers in Sort by / Reorder / etc.
      if (
        actionType === ActionTypes.SUM_VALUES ||
        actionType === ActionTypes.PCT_UPLIFT ||
        actionType === ActionTypes.POINT_CHANGE
      ) {
        mutateLabels()
      }
      fetchActions()
    } catch (error) {
      // Dependency validation failures leave the action untouched; make the server's
      // explanation visible instead of making the delete click appear to do nothing.
      openNotification(getErrorMessage(error))
      console.error(error.response)
    }
  }

  const fetchActions = useCallback(async () => {
    // No data selected → no transformation id; skip the request that would otherwise hit
    // /api/actions/?data_selection_pk=undefined, and leave the queue empty.
    if (transformationId == null) {
      setActionQueue([])
      return
    }
    try {
      const actions = await getDataActions(transformationId)
      setActionQueue(actions)
      //if action list is 0 we have cleared data actions (probably by updating data transform)
      //therefore clear the states
      if (actions.length === 0) {
        setClearOptions({
          filter: true,
          reorder: true,
          reverse: true,
          sort_by: true,
          transpose: true,
          top_and_bottom: true,
          copy_order: true,
          string_filter: true,
          shift_labels: true,
          sum_values: true,
          pct_uplift: true,
          delete_position: true,
          delete_label: true,
          point_change: true,
          round: true
        })
        setInitialValues(null)
      }
    } catch (error) {
      console.error(error.message)
    }
  }, [transformationId])

  useEffect(() => {
    fetchActions()
  }, [fetchActions])

  // Nothing is mapped into this object yet, so there is no data to transform. Show a friendly
  // prompt instead of the (previously crashing) transformation form. Placed after every hook above
  // so the early return does not violate the rules of hooks.
  if (!dataDefinition) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={i18n.t(
          'editor.right-panel.data-source.form.data-transformation.noDataSelected'
        )}
      />
    )
  }

  // The server supplies the frame immediately before an in-sequence net for edit mode.
  // This keeps synthetic net ids server-owned and naturally excludes this net and later nets.
  // Sort by gets it too, regardless of the flag, since it can sort by a net (issue #2464).
  const savedSourceFrame =
    mode === 'update'
      ? (initialValues as any)?.sourceFrame || (initialValues as any)?.source_frame
      : null
  const editSourceFrame = inSequenceNets ? savedSourceFrame : null
  const editNetColumns = editSourceFrame?.columns || netColumns
  const editNetRows = editSourceFrame?.rows || netRows

  return (
    <Spin spinning={loading}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Select<string | number, { value: ActionTypes }>
          showSearch
          // Options render a Tooltip element, not a string, so filter on the label text instead
          filterOption={(input, option) =>
            String(actionMapping[option!.value] ?? '')
              .toLowerCase()
              .includes(input.toLowerCase())
          }
          dropdownMatchSelectWidth={false}
          dropdownAlign={{
            points: ['tr', 'tl'], //align dropdown's top-right to top-left of input element
            offset: [-5, 0], //align offset
            overflow: {
              adjustX: 0,
              adjustY: 0 //do not auto flip in y axis
            }
          }}
          dropdownStyle={{
            display: transformVisible ? 'inline-block' : 'none',
            width: 'fit-content',
            maxWidth: '33%',
            minWidth: '15%'
          }}
          onDropdownVisibleChange={open => setTransformVisible(open)}
          placeholder={i18n.t(
            'editor.right-panel.data-source.form.data-transformation.placeholder'
          )}
          onChange={onSelect}
          value={selectedAction}
          style={{ width: '100%' }}
        >
          {/* Only render actions that belong to this menu. Text-only transforms such as
              string_split live in TextTransformations and have no label here, so skipping
              unmapped types avoids a blank, non-selectable option. */}
          {Object.values(ActionTypes)
            .filter(actionType => actionMapping[actionType])
            .map(actionType => (
              <Select.Option value={actionType} key={actionType}>
                <Tooltip title={tooltips[actionType]}>{actionMapping[actionType]}</Tooltip>
              </Select.Option>
            ))}
        </Select>
      </Space>

      <Reorder
        isVisible={selectedAction === ActionTypes.REORDER}
        columns={resultingColumns}
        rows={resultingRows}
        onSubmit={onSubmit}
        onCancel={onCancel}
        initialValues={initialValues}
        clearOptions={clearOptions.reorder}
      />
      <Reverse
        isVisible={selectedAction === ActionTypes.REVERSE}
        onSubmit={onSubmit}
        onCancel={onCancel}
        initialValues={initialValues}
        clearOptions={clearOptions.reverse}
      />
      <Transpose
        clearOptions={clearOptions.transpose}
        isVisible={selectedAction === ActionTypes.TRANSPOSE}
        onSubmit={onSubmit}
        onCancel={onCancel}
      />
      <SortBy
        columns={savedSourceFrame?.columns || resultingColumns}
        rows={savedSourceFrame?.rows || resultingRows}
        isVisible={selectedAction === ActionTypes.SORTBY}
        onSubmit={onSubmit}
        onCancel={onCancel}
        initialValues={initialValues}
        clearOptions={clearOptions.sort_by}
      />
      <Filter
        columns={columns}
        rows={rows}
        isVisible={selectedAction === ActionTypes.FILTER}
        onSubmit={onSubmit}
        onCancel={onCancel}
        initialValues={initialValues}
        clearOptions={clearOptions.filter}
      />
      <Trim
        columns={resultingColumns}
        rows={resultingRows}
        isVisible={selectedAction === ActionTypes.TOP_AND_BOTTOM}
        onSubmit={onSubmit}
        onCancel={onCancel}
        initialValues={initialValues}
        clearOptions={clearOptions.top_and_bottom}
      />
      <CopyOrder
        columns={resultingColumns}
        rows={resultingRows}
        onCancel={onCancel}
        onSubmit={onCopyOrderSubmit}
        isVisible={selectedAction === ActionTypes.COPY_ORDER}
        clearOptions={clearOptions.copy_order}
      />
      <StringFilter
        columns={resultingColumns}
        rows={resultingRows}
        onCancel={onCancel}
        onSubmit={onSubmit}
        initialValues={initialValues}
        isVisible={selectedAction === ActionTypes.STRING_FILTER}
        clearOptions={clearOptions.string_filter}
      />
      <ShiftLabels
        columns={resultingColumns}
        rows={resultingRows}
        isVisible={selectedAction === ActionTypes.SHIFT_LABELS}
        onSubmit={onSubmit}
        onCancel={onCancel}
        initialValues={initialValues}
        clearOptions={clearOptions.shift_labels}
      />
      <SumValues
        isVisible={selectedAction === ActionTypes.SUM_VALUES}
        onSubmit={onSubmit}
        onCancel={onCancel}
        initialValues={initialValues}
        clearOptions={clearOptions.sum_values}
        columns={editNetColumns}
        rows={editNetRows}
      />
      <PctUplift
        isVisible={selectedAction === ActionTypes.PCT_UPLIFT}
        onSubmit={onSubmit}
        onCancel={onCancel}
        initialValues={initialValues}
        clearOptions={clearOptions.pct_uplift}
        columns={editNetColumns}
        rows={editNetRows}
      />
      <PointChange
        isVisible={selectedAction === ActionTypes.POINT_CHANGE}
        columns={editNetColumns}
        rows={editNetRows}
        onSubmit={onSubmit}
        onCancel={onCancel}
        initialValues={initialValues}
        clearOptions={clearOptions.point_change}
      />
      <Round
        isVisible={selectedAction === ActionTypes.ROUND}
        onSubmit={onSubmit}
        onCancel={onCancel}
        initialValues={initialValues}
        clearOptions={clearOptions.round}
      />
      <DeletePosition
        isVisible={selectedAction === ActionTypes.DELETE_POSITION}
        columns={resultingColumns}
        rows={resultingRows}
        originalColumns={columns}
        originalRows={rows}
        transposed={dataTransformation?.transposed}
        onSubmit={onSubmit}
        onCancel={onCancel}
        initialValues={initialValues}
        clearOptions={clearOptions.delete_position}
      />
      <DeleteByLabel
        isVisible={selectedAction === ActionTypes.DELETE_LABEL}
        columns={resultingColumns}
        rows={resultingRows}
        originalColumns={columns}
        originalRows={rows}
        transposed={dataTransformation?.transposed}
        onSubmit={onSubmit}
        onCancel={onCancel}
        initialValues={initialValues}
        clearOptions={clearOptions.delete_label}
      />
      <ActionList onEdit={onEditAction} onDelete={onDeleteAction} queue={actionQueue} />
    </Spin>
  )
}

export default DataTransformationsSelector
